"use client";

import { createContext, useContext } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { parseComparisonIds } from "@/utils/comparisonUrls";

const ComparisonContext = createContext();

export function ComparisonProvider({ children }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // If we are on /comparison, the source of truth is ?ids=...
  // On all other pages (Search, Home, etc.), the transient selection is ?compare=...
  const isComparisonPage = pathname?.startsWith("/comparison");
  
  const selectedIds = parseComparisonIds(
    searchParams.get(isComparisonPage ? "ids" : "compare") || ""
  );

  const replaceTargetId = searchParams.get("replace");

  const updateUrl = (newIds) => {
    const current = new URLSearchParams(Array.from(searchParams.entries()));
    
    // Clear replace intent if we've fulfilled it
    if (replaceTargetId) {
      current.delete("replace");
    }

    if (newIds.length === 0) {
      current.delete(isComparisonPage ? "ids" : "compare");
    } else {
      current.set(isComparisonPage ? "ids" : "compare", newIds.join(","));
    }

    const search = current.toString();
    const query = search ? `?${search}` : "";
    
    // Use push or replace? push allows back button, replace doesn't clutter history for rapid clicks.
    // Replace is better for selection toggling so we don't spam the history stack.
    router.replace(`${pathname}${query}`, { scroll: false });
  };

  const toggleCompare = (id) => {
    const stringId = String(id);
    let newIds = [...selectedIds];

    if (replaceTargetId) {
      // We are in replace mode
      const targetIndex = newIds.indexOf(replaceTargetId);
      if (targetIndex !== -1) {
        newIds[targetIndex] = stringId; // Swap in place
      } else {
        newIds.push(stringId); // Fallback if missing
      }
      
      // If we just fulfilled a replace, and we are NOT on comparison page,
      // route back to comparison immediately for a smooth UX
      const current = new URLSearchParams(Array.from(searchParams.entries()));
      current.delete("replace");
      current.delete("compare"); // clear transient selection
      current.set("ids", newIds.join(",")); // set final view
      router.push(`/comparison?${current.toString()}`);
      return;
    }

    if (newIds.includes(stringId)) {
      newIds = newIds.filter((existingId) => existingId !== stringId);
    } else {
      if (newIds.length >= 4) {
        alert("Maximum 4 tours can be compared.");
        return;
      }
      newIds.push(stringId);
    }

    updateUrl(newIds);
  };

  const removeCompare = (id) => {
    const stringId = String(id);
    const newIds = selectedIds.filter((existingId) => existingId !== stringId);
    updateUrl(newIds);
  };

  const clearCompare = () => {
    updateUrl([]);
  };

  const setReplaceTargetId = (id) => {
    // Navigate to Search to select the replacement
    const current = new URLSearchParams();
    current.set("replace", id);
    // Keep the current compare selection for context
    current.set("compare", selectedIds.join(","));
    router.push(`/search?${current.toString()}`);
  };

  return (
    <ComparisonContext.Provider
      value={{
        selectedIds,
        replaceTargetId,
        toggleCompare,
        removeCompare,
        clearCompare,
        setReplaceTargetId,
        isComparisonPage,
      }}
    >
      {children}
    </ComparisonContext.Provider>
  );
}

export function useComparisonContext() {
  return useContext(ComparisonContext);
}
